/**
 * AnnouncementDropdown Component
 * 
 * Simple dropdown that shows "What's New" announcements when user clicks the bell icon.
 * Displays text-only content from the announcements config file.
 * No database required - all content comes from src/config/announcements.ts
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { announcementUtils, type AnnouncementItem } from '../../config/announcements';
import { announcementConfigManager } from '../../lib/announcement-config-manager';

interface AnnouncementDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

export const AnnouncementDropdown: React.FC<AnnouncementDropdownProps> = ({
  isOpen,
  onClose,
  anchorRef
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Detect if we're on mobile or PWA
  const isMobileOrPWA = window.innerWidth <= 768 || window.matchMedia('(display-mode: standalone)').matches;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, anchorRef]);

  // Close on escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  const announcement = announcementConfigManager.getCurrentConfig();

  if (!announcement) {
    return null;
  }

  // Shared dropdown content
  const dropdownInnerContent = (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full">
              <Sparkles size={14} className="text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
              {announcement.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            aria-label="Close announcements"
          >
            <X size={14} />
          </button>
        </div>
        
        {/* Version badge */}
        <div className="flex items-center justify-between mt-2">
          <span className="inline-block px-2 py-1 bg-white/60 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">
            {announcement.version}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Updated {announcementUtils.formatDate(announcement.updatedAt)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3 max-h-60 overflow-y-auto">
        <div className="space-y-3">
          {announcement.items.map((item: AnnouncementItem, index: number) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {/* Icon */}
              <div className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-700 rounded-lg shadow-sm flex-shrink-0">
                <span className="text-lg" role="img" aria-label="Feature icon">
                  {item.icon || '✨'}
                </span>
              </div>
              
              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {item.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty state */}
        {announcement.items.length === 0 && (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
              <Sparkles size={20} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No announcements at this time
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {announcement.items.length} update{announcement.items.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => {
              announcementConfigManager.dismissCurrentAnnouncement();
              onClose();
            }}
            className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium"
          >
            Got it
          </button>
        </div>
      </div>
    </>
  );

  const dropdownContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Full-screen backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 bg-black/20 z-40 ${isMobileOrPWA ? '' : 'md:hidden'}`}
            onClick={onClose}
          />

          {/* Mobile/PWA centered modal */}
          {isMobileOrPWA ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden w-80 max-w-[90vw]"
                style={{ transformOrigin: 'center' }}
              >
                {dropdownInnerContent}
              </motion.div>
            </div>
          ) : (
            /* Desktop dropdown */
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute top-full right-0 mt-2 w-80 max-w-[90vw] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
              style={{ transformOrigin: 'top right' }}
            >
              {dropdownInnerContent}
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  // Use portal for mobile/PWA to escape container clipping, regular render for desktop
  if (isMobileOrPWA) {
    return createPortal(dropdownContent, document.body);
  }
  
  return dropdownContent;
};

export default AnnouncementDropdown;